/**
 * The schema-v8 controls of one expected-sequence step, and the post-release
 * budget of the program they form.
 *
 * Controlled components: the Channel Modifiers step owns the step rows and the
 * budget, and these render what a row's kind and position allow — the rules
 * live in `channelModifierV8`. Every control carries `data-doc-label`, so the
 * documentation scanner keys it by its caption rather than by its position.
 */
import React from "react";
import { Checkbox, Input, InputNumber, Select } from "antd";
import { useTranslation } from "react-i18next";
import { ActiveState } from "../../../../flatbuffers/generated/archive/active-state";
import { SequenceStepKind } from "../../../../flatbuffers/generated/archive/sequence-step-kind";
import { StepPositionSource } from "../../../../flatbuffers/generated/archive/step-position-source";
import { isFloorSourceVisible, type ProgramFormStep } from "../channelModifierV8";

/** A labelled option of a channel dropdown. */
export interface ChannelChoice {
  label: string;
  value: string;
}

interface ProgramStepFieldsProps<S extends ProgramFormStep> {
  step: S;
  index: number;
  /**
   * The modifier can run a program (an Outbound Before): every kind and field.
   * Otherwise only the kind is shown, held to Wait.
   */
  program: boolean;
  /** The step comes after the RELEASE step, so it may be marked always-run. */
  postRelease: boolean;
  /** Channel choices for a WAIT step (`stepObserveOptions`). */
  observeOptions: ChannelChoice[];
  /** Channel choices for a SEND step (`stepTargetOptions`). */
  targetOptions: ChannelChoice[];
  onChange: (step: S) => void;
}

const fieldStyle: React.CSSProperties = { minWidth: 160, flex: 1 };
const captionStyle: React.CSSProperties = { display: "block" };

/** One step's kind, channel, floor source, flags and label. */
export default function ProgramStepFields<S extends ProgramFormStep>({
  step,
  index,
  program,
  postRelease,
  observeOptions,
  targetOptions,
  onChange,
}: ProgramStepFieldsProps<S>): React.JSX.Element {
  const [t] = useTranslation();
  const isWait = step.kind === SequenceStepKind.WAIT;
  const isSend = step.kind === SequenceStepKind.SEND;
  return (
    <div
      data-testid={`modifier-step-program-${index}`}
      style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 4 }}
    >
      <div style={fieldStyle}>
        <span className="generalStyles__inputLabel" style={captionStyle}>
          {t("Kind")}
        </span>
        <Select
          data-doc-label="Step Kind"
          data-testid={`modifier-step-kind-${index}`}
          style={{ width: "100%" }}
          value={step.kind}
          onChange={(v: SequenceStepKind) => onChange({ ...step, kind: v })}
          options={[
            { label: "Wait", value: SequenceStepKind.WAIT },
            { label: "Release", value: SequenceStepKind.RELEASE, disabled: !program },
            { label: "Send", value: SequenceStepKind.SEND, disabled: !program },
            {
              label: "Await acknowledgement",
              value: SequenceStepKind.AWAIT_ACK,
              disabled: !program,
            },
          ]}
        />
      </div>
      {program && isWait && (
        <div style={fieldStyle}>
          <span className="generalStyles__inputLabel" style={captionStyle}>
            {t("Observe On")}
          </span>
          <Select
            data-doc-label="Step Observe Channel"
            data-testid={`modifier-step-observe-${index}`}
            style={{ width: "100%" }}
            value={step.observeChannelId}
            options={observeOptions}
            onChange={(v: string) => onChange({ ...step, observeChannelId: v ?? "" })}
          />
        </div>
      )}
      {program && isSend && (
        <div style={fieldStyle}>
          <span className="generalStyles__inputLabel" style={captionStyle}>
            {t("Send To")}
          </span>
          <Select
            data-doc-label="Step Send Channel"
            data-testid={`modifier-step-send-to-${index}`}
            style={{ width: "100%" }}
            value={step.targetChannelId}
            options={targetOptions}
            onChange={(v: string) => onChange({ ...step, targetChannelId: v ?? "" })}
          />
        </div>
      )}
      {program && isSend && (
        <div style={fieldStyle}>
          <span className="generalStyles__inputLabel" style={captionStyle}>
            {t("Send State")}
          </span>
          <Select
            data-doc-label="Step Send State"
            data-testid={`modifier-step-send-state-${index}`}
            style={{ width: "100%" }}
            // UNSET sends active true, the same as Active.
            value={
              step.sendActive === ActiveState.INACTIVE ? ActiveState.INACTIVE : ActiveState.ACTIVE
            }
            onChange={(v: ActiveState) => onChange({ ...step, sendActive: v })}
            options={[
              { label: "Active", value: ActiveState.ACTIVE },
              { label: "Inactive", value: ActiveState.INACTIVE },
            ]}
          />
        </div>
      )}
      {program && isFloorSourceVisible(step) && (
        <div style={fieldStyle}>
          <span className="generalStyles__inputLabel" style={captionStyle}>
            {t("Floor Source")}
          </span>
          <Select
            data-doc-label="Step Floor Source"
            data-testid={`modifier-step-floor-source-${index}`}
            style={{ width: "100%" }}
            value={step.positionSource}
            onChange={(v: StepPositionSource) => onChange({ ...step, positionSource: v })}
            options={[
              { label: "Payload's floor", value: StepPositionSource.PAYLOAD },
              { label: "Command's floor", value: StepPositionSource.SOURCE },
            ]}
          />
        </div>
      )}
      {program && isWait && (
        <Checkbox
          data-doc-label="Pass If Already Current"
          data-testid={`modifier-step-if-current-${index}`}
          checked={step.satisfiedIfCurrent}
          onChange={(e) => onChange({ ...step, satisfiedIfCurrent: e.target.checked })}
        >
          {t("Pass if already current")}
        </Checkbox>
      )}
      {program && postRelease && (
        <Checkbox
          data-doc-label="Always Run"
          data-testid={`modifier-step-always-run-${index}`}
          checked={step.alwaysRun}
          onChange={(e) => onChange({ ...step, alwaysRun: e.target.checked })}
        >
          {t("Always run")}
        </Checkbox>
      )}
      {program && (
        <div style={fieldStyle}>
          <span className="generalStyles__inputLabel" style={captionStyle}>
            {t("Label")}
          </span>
          <Input
            data-doc-label="Step Label"
            data-testid={`modifier-step-label-${index}`}
            value={step.label}
            placeholder={t("Shown for this step in Command Sequences")}
            onChange={(e) => onChange({ ...step, label: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

interface PostReleaseBudgetFieldProps {
  value: number;
  onChange: (ms: number) => void;
}

/** The largest budget the wire field (uint32 milliseconds) can carry. */
const MAX_BUDGET_MS = 4_294_967_295;

/** The time allowed for the steps after the RELEASE step. 0 means VTS's default. */
export function PostReleaseBudgetField({
  value,
  onChange,
}: PostReleaseBudgetFieldProps): React.JSX.Element {
  const [t] = useTranslation();
  return (
    <div style={{ marginTop: 8 }}>
      <span className="generalStyles__inputLabel">{t("Post-Release Budget (ms)")}</span>
      <InputNumber
        data-doc-label="Post-Release Budget (ms)"
        data-testid="modifier-post-release-budget"
        style={{ width: "100%", marginTop: 4 }}
        min={0}
        max={MAX_BUDGET_MS}
        step={1000}
        value={value}
        onChange={(v) =>
          onChange(typeof v === "number" ? Math.min(Math.max(Math.trunc(v), 0), MAX_BUDGET_MS) : 0)
        }
      />
      <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
        {t(
          "Time allowed for the steps after the Release step, which run without holding the source channel. 0 uses VTS's default of 10 minutes.",
        )}
      </div>
    </div>
  );
}
